import React from 'react';
import { Text, View } from '@react-pdf/renderer';
import { sensorUnits } from 'commons/src/constants';
import { Rating } from 'commons/src/models/commonEnums';
import { ThresholdRange } from 'commons/src/models/commonTypeScript';
import { sensorRating, sensorShowsRatingLabels } from 'commons/src/sensorRatingUtils';
import { PDFContext, ReportSensorThresholds } from '../../../../../models/common';
import { pdfColors } from '../utils/pdfColors';
import {
    formatSensorUnit,
    getStatusColor,
    SpaceMeasurementData,
    SpaceMeasurementDataColumns,
} from '../utils/pdfHelpers';
import iaqInsight2PdfStyles from '../utils/pdfStyles';

type IaqInsight2DataTableProps = {
    translationFunction: PDFContext;
    sensorType: string;
    sensorThresholdConfiguration: ReportSensorThresholds;
    spaceMeasurementColumns: SpaceMeasurementDataColumns;
};

const superscriptNumbers = ['¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'];

const getThresholdKey = (thresholds: ReportSensorThresholds): string => {
    const rangeKey = thresholds.ranges
        .map(r => `${r.from}-${r.to}-${r.rating}`)
        .sort()
        .join('|');
    return `${rangeKey}:${thresholds.unit}`;
};

const formatThresholdDescription = (ranges: ThresholdRange[], unit: string): string => {
    const unitSymbol = sensorUnits[unit as keyof typeof sensorUnits] || unit;
    const sortedRanges = [...ranges].sort((a, b) => (a.from ?? -Infinity) - (b.from ?? -Infinity));

    return sortedRanges
        .map(range => {
            const ratingLabel = range.rating;
            if (range.from === undefined || range.from === null) {
                return `${ratingLabel} below ${range.to}${unitSymbol}`;
            }
            if (range.to === undefined || range.to === null) {
                return `${ratingLabel} above ${range.from}${unitSymbol}`;
            }
            return `${ratingLabel} between ${range.from} and ${range.to}${unitSymbol}`;
        })
        .join(', ');
};

const IaqInsight2DataTable = ({
    translationFunction,
    sensorType,
    sensorThresholdConfiguration,
    spaceMeasurementColumns,
}: IaqInsight2DataTableProps): React.ReactElement | null => {
    const { ranges: thresholdRanges, unit: measurementUnit } = sensorThresholdConfiguration;
    const formattedMeasurementUnit = formatSensorUnit(sensorType, measurementUnit);

    // Find unique custom thresholds (different from default sensor threshold)
    const uniqueThresholds: { thresholds: ReportSensorThresholds; spaceNames: string[] }[] = [];
    const thresholdMap = new Map<string, { thresholds: ReportSensorThresholds; spaceNames: string[] }>();

    spaceMeasurementColumns.forEach(row => {
        if (row.thresholds) {
            const key = getThresholdKey(row.thresholds);
            const existing = thresholdMap.get(key);
            if (existing) {
                existing.spaceNames.push(row.name);
            } else {
                thresholdMap.set(key, { thresholds: row.thresholds, spaceNames: [row.name] });
            }
        }
    });

    thresholdMap.forEach(value => {
        uniqueThresholds.push(value);
    });

    const getFootnoteNumber = (thresholds: ReportSensorThresholds | undefined): number | null => {
        if (!thresholds) return null;
        const key = getThresholdKey(thresholds);
        const index = uniqueThresholds.findIndex(ut => getThresholdKey(ut.thresholds) === key);
        return index >= 0 ? index + 1 : null;
    };

    const getValueStatusWithRating = (
        measurementValue: number | undefined
    ): { value: string; unit: string; rating: Rating | undefined; color: string } | null => {
        if (measurementValue === undefined) {
            return null;
        }
        const measurementValueRating = sensorRating(thresholdRanges, measurementValue);
        const statusColorForValue = measurementValueRating
            ? getStatusColor(thresholdRanges, measurementValue)
            : pdfColors.blackShark;
        return {
            value: String(measurementValue),
            unit: formattedMeasurementUnit,
            rating: measurementValueRating,
            color: statusColorForValue,
        };
    };

    const hasNoData = (row: SpaceMeasurementData): boolean => {
        return row.average === undefined && row.lowestMeasurement === undefined && row.highestMeasurement === undefined;
    };

    const spacesWithNoData = spaceMeasurementColumns.filter(hasNoData);

    // Don't render table if there are no spaces at all
    if (!spaceMeasurementColumns || spaceMeasurementColumns.length === 0) {
        return null;
    }

    return (
        <View style={iaqInsight2PdfStyles.tableContainer}>
            {/* Table Rows - header only shown once at the start */}
            {spaceMeasurementColumns.map((spaceMeasurementData: SpaceMeasurementData, index: number) => {
                const averageValueStatus = getValueStatusWithRating(spaceMeasurementData.average);
                const lowestMeasurementStatus = getValueStatusWithRating(spaceMeasurementData.lowestMeasurement);
                const highestMeasurementStatus = getValueStatusWithRating(spaceMeasurementData.highestMeasurement);

                const showHeader = index === 0;

                return (
                    <View key={`${spaceMeasurementData.serialNumber}-${sensorType}`} wrap={false}>
                        {/* Table Header - shown before first row and every 12 rows for pagination */}
                        {showHeader && (
                            <View style={iaqInsight2PdfStyles.tableHeaderRow}>
                                <View style={[iaqInsight2PdfStyles.tableHeaderCell, { width: '10%' }]}>
                                    <Text style={iaqInsight2PdfStyles.tableHeaderText}>
                                        {translationFunction('IAQReport2.Floor')}
                                    </Text>
                                </View>
                                <View style={[iaqInsight2PdfStyles.tableHeaderCell, { width: '30%' }]}>
                                    <Text style={iaqInsight2PdfStyles.tableHeaderText}>
                                        {translationFunction('Space.SpaceName')}
                                    </Text>
                                </View>
                                <View style={[iaqInsight2PdfStyles.tableHeaderCell, { width: '20%' }]}>
                                    <Text style={iaqInsight2PdfStyles.tableHeaderText}>
                                        {translationFunction('IAQReport2.AverageValue')}
                                    </Text>
                                </View>
                                <View style={[iaqInsight2PdfStyles.tableHeaderCell, { width: '20%' }]}>
                                    <Text style={iaqInsight2PdfStyles.tableHeaderText}>
                                        {translationFunction('IAQReport2.LowestMeasurement')}
                                    </Text>
                                </View>
                                <View style={[iaqInsight2PdfStyles.tableHeaderCell, { width: '20%' }]}>
                                    <Text style={iaqInsight2PdfStyles.tableHeaderText}>
                                        {translationFunction('IAQReport2.HighestMeasurement')}
                                    </Text>
                                </View>
                            </View>
                        )}

                        <View style={iaqInsight2PdfStyles.tableRow}>
                            {/* Floor */}
                            <View style={[iaqInsight2PdfStyles.tableCell, { width: '10%' }]}>
                                <Text style={iaqInsight2PdfStyles.tableCellText}>
                                    {spaceMeasurementData.floor !== undefined
                                        ? spaceMeasurementData.floor.toString()
                                        : '-'}
                                </Text>
                            </View>

                            {/* Space name with footnote markers */}
                            <View style={[iaqInsight2PdfStyles.tableCell, { width: '30%' }]}>
                                <Text style={iaqInsight2PdfStyles.spaceNameText}>
                                    {spaceMeasurementData.name}
                                    {hasNoData(spaceMeasurementData) && '*'}
                                    {spaceMeasurementData.thresholds &&
                                        ((): string => {
                                            const footnoteNum = getFootnoteNumber(spaceMeasurementData.thresholds);
                                            return footnoteNum
                                                ? superscriptNumbers[footnoteNum - 1] || String(footnoteNum)
                                                : '';
                                        })()}
                                </Text>
                            </View>

                            {/* Average */}
                            <View style={[iaqInsight2PdfStyles.tableCell, { width: '20%' }]}>
                                {averageValueStatus ? (
                                    <View>
                                        <Text style={iaqInsight2PdfStyles.tableCellText}>
                                            {averageValueStatus.value}
                                            {averageValueStatus.unit && (
                                                <Text style={iaqInsight2PdfStyles.tableCellUnit}>
                                                    {' '}
                                                    {averageValueStatus.unit}
                                                </Text>
                                            )}
                                        </Text>
                                        {sensorShowsRatingLabels(sensorType) && averageValueStatus.rating && (
                                            <Text
                                                style={[
                                                    iaqInsight2PdfStyles.ratingText,
                                                    { color: averageValueStatus.color },
                                                ]}
                                            >
                                                {translationFunction(`Ratings.${averageValueStatus.rating}`)}
                                            </Text>
                                        )}
                                    </View>
                                ) : (
                                    <Text style={iaqInsight2PdfStyles.tableCellText}>-</Text>
                                )}
                            </View>

                            {/* Lowest */}
                            <View style={[iaqInsight2PdfStyles.tableCell, { width: '20%' }]}>
                                {lowestMeasurementStatus ? (
                                    <View>
                                        <Text style={iaqInsight2PdfStyles.tableCellText}>
                                            {lowestMeasurementStatus.value}
                                            {lowestMeasurementStatus.unit && (
                                                <Text style={iaqInsight2PdfStyles.tableCellUnit}>
                                                    {' '}
                                                    {lowestMeasurementStatus.unit}
                                                </Text>
                                            )}
                                        </Text>
                                        {sensorShowsRatingLabels(sensorType) && lowestMeasurementStatus.rating && (
                                            <Text
                                                style={[
                                                    iaqInsight2PdfStyles.ratingText,
                                                    { color: lowestMeasurementStatus.color },
                                                ]}
                                            >
                                                {translationFunction(`Ratings.${lowestMeasurementStatus.rating}`)}
                                            </Text>
                                        )}
                                    </View>
                                ) : (
                                    <Text style={iaqInsight2PdfStyles.tableCellText}>-</Text>
                                )}
                            </View>

                            {/* Highest */}
                            <View style={[iaqInsight2PdfStyles.tableCell, { width: '20%' }]}>
                                {highestMeasurementStatus ? (
                                    <View>
                                        <Text style={iaqInsight2PdfStyles.tableCellText}>
                                            {highestMeasurementStatus.value}
                                            {highestMeasurementStatus.unit && (
                                                <Text style={iaqInsight2PdfStyles.tableCellUnit}>
                                                    {' '}
                                                    {highestMeasurementStatus.unit}
                                                </Text>
                                            )}
                                        </Text>
                                        {sensorShowsRatingLabels(sensorType) && highestMeasurementStatus.rating && (
                                            <Text
                                                style={[
                                                    iaqInsight2PdfStyles.ratingText,
                                                    { color: highestMeasurementStatus.color },
                                                ]}
                                            >
                                                {translationFunction(`Ratings.${highestMeasurementStatus.rating}`)}
                                            </Text>
                                        )}
                                    </View>
                                ) : (
                                    <Text style={iaqInsight2PdfStyles.tableCellText}>-</Text>
                                )}
                            </View>
                        </View>
                    </View>
                );
            })}

            {/* Footnotes section */}
            {(spacesWithNoData.length > 0 || uniqueThresholds.length > 0) && (
                <View style={{ marginTop: 8 }}>
                    {/* Footnote for spaces without data */}
                    {spacesWithNoData.length > 0 && (
                        <Text style={{ fontSize: 9, color: pdfColors.greyText, marginBottom: 4 }}>
                            * {translationFunction('IAQReport2.NoDataInSelectedTimePeriod')}
                        </Text>
                    )}
                    {/* Footnotes for custom thresholds */}
                    {uniqueThresholds.map((ut, index) => {
                        const description = formatThresholdDescription(
                            ut.thresholds.ranges,
                            String(ut.thresholds.unit)
                        );
                        return (
                            <Text
                                key={getThresholdKey(ut.thresholds)}
                                style={{ fontSize: 9, color: pdfColors.greyText, marginBottom: 4 }}
                            >
                                {superscriptNumbers[index] || String(index + 1)} {ut.spaceNames.join(', ')}:{' '}
                                {translationFunction('IAQReport2.CustomThresholdsSetTo')} {description}.
                            </Text>
                        );
                    })}
                </View>
            )}
        </View>
    );
};

export default IaqInsight2DataTable;
